import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Chip
} from '@material-ui/core';
import {
  AccountCircle,
  ExitToApp,
  Person
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { images } from '../../assets';

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    borderBottom: '1px solid #e0e0e0',
  },
  toolbar: {
    minHeight: '70px',
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    height: '60px',
    width: 'auto',
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#6a1b9a',
    letterSpacing: '0.5px',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginLeft: 'auto',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginRight: theme.spacing(1),
  },
  userName: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#333',
    lineHeight: 1.2,
  },
  userEmail: {
    fontSize: '0.75rem',
    color: '#666',
    lineHeight: 1.2,
  },
  avatar: {
    backgroundColor: '#6a1b9a',
    width: '40px',
    height: '40px',
    fontSize: '1rem',
    fontWeight: 600,
  },
  menuItem: {
    minWidth: '200px',
    padding: theme.spacing(1.5),
  },
  menuHeader: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
  },
  environmentChip: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    fontSize: '0.7rem',
    height: '24px',
    fontWeight: 500,
  }
}));

const SSOHeader = ({ userDetails }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    // Handle client SSO integration logout
    if (window.location.pathname.includes("client-sso-integration")) {
      window.location.href = process.env.REACT_APP_CLIENT_SSO_URL;
      localStorage.clear();
      localStorage.removeItem("user");
    } else {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      localStorage.clear();
      localStorage.removeItem("user");
    }
  };

  // Extract user email from various sources
  const getUserEmail = () => {
    // Try multiple sources for user email
    const email = (userDetails && userDetails.email) ||
                  localStorage.getItem('userEmail') ||
                  localStorage.getItem('email') ||
                  '';

    // Try to extract from JWT token if available
    if (!email) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = parts[1];
            const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
            const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
            const claims = JSON.parse(decoded);
            return claims.email || claims.preferred_username || claims.upn || claims.unique_name || 'user@company.com';
          }
        }
      } catch (error) {
        // console.log('Could not extract email from token');
      }
    }

    return email || 'user@company.com';
  };

  // Extract user initials
  const getUserInitials = () => {
    const email = getUserEmail();
    if (!email || email === 'user@company.com') return 'U';

    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email[0].toUpperCase();
  };

  // Get display name
  const getDisplayName = () => {
    const email = getUserEmail();

    if (userDetails && userDetails.name) return userDetails.name;
    if (userDetails && userDetails.given_name && userDetails.family_name) {
      return `${userDetails.given_name} ${userDetails.family_name}`;
    }

    // Try to extract from JWT token
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = parts[1];
          const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
          const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
          const claims = JSON.parse(decoded);
          if (claims.name) return claims.name;
          if (claims.given_name) return `${claims.given_name} ${claims.family_name || ''}`.trim();
        }
      }
    } catch (error) {
      // console.log('Could not extract name from token');
    }

    if (email && email !== 'user@company.com') {
      const name = email.split('@')[0].replace(/\./g, ' ');
      return name.split(' ').map(part =>
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ');
    }
    return 'User';
  };

  const environment = process.env.REACT_APP_CAMENVPORTAL || 'DEV';

  return (
    <AppBar position="static" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        {/* Logo and Title */}
        <Box display="flex" alignItems="center">
          <img
            src={images.NavLogoImage}
            alt="Application Logo"
            className={classes.logo}
          />
          <Typography variant="h6" className={classes.title}>
            Client SSO Integration Wizard
          </Typography>
        </Box>

        {/* User Section */}
        <Box className={classes.userSection}>
          <Box className={classes.userInfo}>
            <Typography className={classes.userName}>
              {getDisplayName()}
            </Typography>
            <Typography className={classes.userEmail}>
              {getUserEmail()}
            </Typography>
          </Box>

          <IconButton
            onClick={handleMenu}
            color="inherit"
            style={{ padding: '4px' }}
          >
            <Avatar className={classes.avatar}>
              {getUserInitials()}
            </Avatar>
          </IconButton>

          {/* User Menu */}
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {/* User Info Header */}
            <Box className={classes.menuHeader}>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar className={classes.avatar} style={{ width: '32px', height: '32px', fontSize: '0.875rem' }}>
                  {getUserInitials()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {getDisplayName()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getUserEmail()}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            <MenuItem onClick={handleLogout} className={classes.menuItem}>
              <ExitToApp style={{ marginRight: 8, color: '#f44336' }} />
              <Typography style={{ color: '#f44336' }}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const mapStateToProps = (state) => ({
  userDetails: state.auth && state.auth.userDetails ? state.auth.userDetails : {},
});

export default connect(mapStateToProps)(SSOHeader);